/* GdprConsentDialog — explicit medical-data consent (iter420.3 Phase 3).
 *
 * Показва се при ПЪРВО опит за добавяне на personal medical data
 * (conditions, symptoms, family history, lab OCR). Записва timestamp +
 * purposes на server чрез POST /health-intelligence/consent.
 *
 * GDPR Art. 9 (special categories of data): здравните данни изискват
 * explicit consent, не legitimate interest. Затова имаме отделен dialog.
 */
import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Lock, Trash2, X, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function GdprConsentDialog({ open, onClose, onGranted }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [submitting, setSubmitting] = useState(false);
  const [purposes, setPurposes] = useState({
    personalization: true,
    ai_analysis: true,
    research_aggregated: false,
  });

  if (!open) return null;

  const togglePurpose = (key) => setPurposes((p) => ({ ...p, [key]: !p[key] }));

  const handleGrant = async () => {
    setSubmitting(true);
    try {
      const grantedPurposes = Object.entries(purposes).filter(([, v]) => v).map(([k]) => k);
      await axios.post(`${API}/health-intelligence/consent`, {
        granted: true,
        purposes: grantedPurposes,
      });
      toast.success(bg ? "Съгласието е записано ✓" : "Consent recorded ✓");
      onGranted?.(grantedPurposes);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    toast.info(bg ? "Без съгласие не можем да активираме медицинските модули." : "Without consent we can't enable medical modules.");
    onClose?.();
  };

  const purposeMeta = [
    {
      key: "personalization",
      icon: Shield,
      title_bg: "Персонализация на препоръки",
      title_en: "Personalized recommendations",
      desc_bg: "Използваме твоите conditions/symptoms за персонализирани хранителни препоръки.",
      desc_en: "Use your conditions/symptoms to personalize food recommendations.",
      required: true,
    },
    {
      key: "ai_analysis",
      icon: Lock,
      title_bg: "AI анализ от ботове",
      title_en: "AI analysis by bots",
      desc_bg: "NutriBot/EndoBot/NephroBot/HemoBot ще получат твоя контекст за анализ. Данните се изпращат към OpenAI API (US/EU servers).",
      desc_en: "NutriBot/EndoBot/NephroBot/HemoBot will receive your context for analysis. Data is sent to OpenAI API (US/EU servers).",
      required: false,
    },
    {
      key: "research_aggregated",
      icon: Shield,
      title_bg: "Aгрегирани изследвания (опционално)",
      title_en: "Aggregated research (optional)",
      desc_bg: "Можеш да помогнеш на ендокринна научна общност чрез АНОНИМИЗИРАНИ aggregated данни. Никога индивидуални.",
      desc_en: "Help the endocrine research community via ANONYMIZED aggregated data. Never individual.",
      required: false,
    },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
         data-testid="gdpr-consent-dialog" onClick={onClose}>
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl ${
              isDark ? "bg-[hsl(223,47%,9%)] border border-white/10" : "bg-white border border-slate-200"
            }`} onClick={(e) => e.stopPropagation()}>
        <div className={`sticky top-0 z-10 flex items-start justify-between gap-3 px-5 py-4 border-b ${
              isDark ? "bg-[hsl(223,47%,9%)] border-white/10" : "bg-white border-slate-200"
            }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white`}>
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "GDPR съгласие за медицински данни" : "GDPR Consent for medical data"}
              </h2>
              <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {bg ? "Член 9 от GDPR — изрично съгласие" : "GDPR Art. 9 — explicit consent"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-slate-600"}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Privacy guarantees */}
          <div className={`p-3 rounded-xl ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"}`}>
            <p className={`text-xs leading-relaxed ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
              <strong>{bg ? "Гаранции за поверителност:" : "Privacy guarantees:"}</strong>
              <br />• {bg ? "Данните се пазят криптирани в MongoDB Atlas (KMS encryption-at-rest)." : "Data stored encrypted in MongoDB Atlas (KMS encryption-at-rest)."}
              <br />• {bg ? "Само ти имаш достъп — никой служител на Nutramilo не може да види данните без съдебно разпореждане." : "Only you have access — no Nutramilo employee can view without legal order."}
              <br />• {bg ? "По всяко време можеш да оттеглиш съгласието и да изтриеш данните (бутон 'Изтрий профил')." : "You can withdraw consent and delete data at any time ('Delete profile' button)."}
              <br />• {bg ? "Не споделяме с трети страни за маркетинг. Никога." : "Never shared with third parties for marketing."}
            </p>
          </div>

          {/* Purposes */}
          <div className="space-y-2">
            <h3 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg ? "За какво ще се ползват данните" : "How data will be used"}
            </h3>
            {purposeMeta.map((p) => {
              const Icon = p.icon;
              return (
                <Card key={p.key}
                      data-testid={`gdpr-purpose-${p.key}`}
                      className={`border ${
                        purposes[p.key]
                          ? (isDark ? "border-emerald-500/40 bg-emerald-500/5" : "border-emerald-300 bg-emerald-50/40")
                          : (isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white")
                      } cursor-pointer transition-colors`}
                      onClick={() => !p.required && togglePurpose(p.key)}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={purposes[p.key]}
                        disabled={p.required}
                        onCheckedChange={() => !p.required && togglePurpose(p.key)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
                          <h4 className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                            {bg ? p.title_bg : p.title_en}
                          </h4>
                          {p.required && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                                  isDark ? "bg-rose-500/20 text-rose-300" : "bg-rose-100 text-rose-700"
                                }`}>
                              {bg ? "ЗАДЪЛЖИТЕЛНО" : "REQUIRED"}
                            </span>
                          )}
                        </div>
                        <p className={`text-[11px] mt-1 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                          {bg ? p.desc_bg : p.desc_en}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div className={`flex items-start gap-2 p-3 rounded-lg ${isDark ? "bg-amber-500/10 border border-amber-500/30" : "bg-amber-50 border border-amber-200"}`}>
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`} />
            <p className={`text-[11px] leading-relaxed ${isDark ? "text-amber-100" : "text-amber-900"}`}>
              {bg
                ? "Nutramilo НЕ е медицинско устройство (CE / MDR). AI анализите са образователни. При сериозни симптоми или промени се консултирай със специалист."
                : "Nutramilo is NOT a medical device (CE / MDR). AI analyses are educational only. For serious symptoms or changes, consult a specialist."}
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <Button variant="outline" onClick={handleDecline} disabled={submitting} className="flex-1"
                    data-testid="gdpr-decline-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              {bg ? "Откажи" : "Decline"}
            </Button>
            <Button onClick={handleGrant} disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    data-testid="gdpr-grant-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              {bg ? "Давам съгласие" : "Grant consent"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
